/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { ClientRepresentative } from '../models/client-representative';
import { Error } from '../models/error';


/**
 * Client Representatives API
 */
@Injectable({
  providedIn: 'root',
})
export class ClientRepresentativesService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation getClientRepresentatives
   */
  static readonly GetClientRepresentativesPath = '/clients/{client_id}/representatives';

  /**
   * Get representatives for a client.
   *
   * Returns a list of client representatives for the given client.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getClientRepresentatives()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientRepresentatives$Response(params: {
    client_id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Array<ClientRepresentative>>> {

    const rb = new RequestBuilder(this.rootUrl, ClientRepresentativesService.GetClientRepresentativesPath, 'get');
    if (params) {
      rb.path('client_id', params.client_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ClientRepresentative>>;
      })
    );
  }

  /**
   * Get representatives for a client.
   *
   * Returns a list of client representatives for the given client.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getClientRepresentatives$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientRepresentatives(params: {
    client_id: number;
    context?: HttpContext
  }
): Observable<Array<ClientRepresentative>> {

    return this.getClientRepresentatives$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ClientRepresentative>>) => r.body as Array<ClientRepresentative>)
    );
  }

  /**
   * Path part for operation createClientRepresentative
   */
  static readonly CreateClientRepresentativePath = '/clients/{client_id}/representatives';

  /**
   * Create a representative for a client.
   *
   * Create a representative for a client
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createClientRepresentative()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClientRepresentative$Response(params: {
    client_id: number;
    context?: HttpContext
    body: ClientRepresentative
  }
): Observable<StrictHttpResponse<ClientRepresentative>> {

    const rb = new RequestBuilder(this.rootUrl, ClientRepresentativesService.CreateClientRepresentativePath, 'post');
    if (params) {
      rb.path('client_id', params.client_id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ClientRepresentative>;
      })
    );
  }

  /**
   * Create a representative for a client.
   *
   * Create a representative for a client
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createClientRepresentative$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClientRepresentative(params: {
    client_id: number;
    context?: HttpContext
    body: ClientRepresentative
  }
): Observable<ClientRepresentative> {

    return this.createClientRepresentative$Response(params).pipe(
      map((r: StrictHttpResponse<ClientRepresentative>) => r.body as ClientRepresentative)
    );
  }

  /**
   * Path part for operation getClientRepresentative
   */
  static readonly GetClientRepresentativePath = '/clients/{client_id}/representatives/{id}';

  /**
   * Get one representative.
   *
   * Get one representative
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getClientRepresentative()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientRepresentative$Response(params: {
    client_id: number;
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ClientRepresentative>> {

    const rb = new RequestBuilder(this.rootUrl, ClientRepresentativesService.GetClientRepresentativePath, 'get');
    if (params) {
      rb.path('client_id', params.client_id, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ClientRepresentative>;
      })
    );
  }

  /**
   * Get one representative.
   *
   * Get one representative
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getClientRepresentative$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientRepresentative(params: {
    client_id: number;
    id: number;
    context?: HttpContext
  }
): Observable<ClientRepresentative> {

    return this.getClientRepresentative$Response(params).pipe(
      map((r: StrictHttpResponse<ClientRepresentative>) => r.body as ClientRepresentative)
    );
  }

  /**
   * Path part for operation updateClientRepresentative
   */
  static readonly UpdateClientRepresentativePath = '/clients/{client_id}/representatives/{id}';

  /**
   * Update one representative.
   *
   * Update one representative
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateClientRepresentative()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClientRepresentative$Response(params: {
    client_id: number;
    id: number;
    context?: HttpContext
    body: ClientRepresentative
  }
): Observable<StrictHttpResponse<ClientRepresentative>> {

    const rb = new RequestBuilder(this.rootUrl, ClientRepresentativesService.UpdateClientRepresentativePath, 'put');
    if (params) {
      rb.path('client_id', params.client_id, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ClientRepresentative>;
      })
    );
  }

  /**
   * Update one representative.
   *
   * Update one representative
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateClientRepresentative$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClientRepresentative(params: {
    client_id: number;
    id: number;
    context?: HttpContext
    body: ClientRepresentative
  }
): Observable<ClientRepresentative> {

    return this.updateClientRepresentative$Response(params).pipe(
      map((r: StrictHttpResponse<ClientRepresentative>) => r.body as ClientRepresentative)
    );
  }

  /**
   * Path part for operation deleteClientRepresentative
   */
  static readonly DeleteClientRepresentativePath = '/clients/{client_id}/representatives/{id}';

  /**
   * Delete the representative.
   *
   * This endpoint is not implemented and will return a 501 error. Deleting client representatives is not supported.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteClientRepresentative()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteClientRepresentative$Response(params: {
    client_id: number;
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Error>> {

    const rb = new RequestBuilder(this.rootUrl, ClientRepresentativesService.DeleteClientRepresentativePath, 'delete');
    if (params) {
      rb.path('client_id', params.client_id, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Error>;
      })
    );
  }

  /**
   * Delete the representative.
   *
   * This endpoint is not implemented and will return a 501 error. Deleting client representatives is not supported.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteClientRepresentative$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteClientRepresentative(params: {
    client_id: number;
    id: number;
    context?: HttpContext
  }
): Observable<Error> {

    return this.deleteClientRepresentative$Response(params).pipe(
      map((r: StrictHttpResponse<Error>) => r.body as Error)
    );
  }

}
